import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { backendUrl } from '../utils/backendUrl';
import UserTimezoneSelect from './UserTimezoneSelect';
import PasswordHint from './PasswordHint';
import './Login.css';

const AcceptInvite = () => {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token') || '';
    const navigate = useNavigate();
    const [preview, setPreview] = useState(null);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [formData, setFormData] = useState({
        password: '',
        confirmPassword: ''
    });

    useEffect(() => {
        if (!token) {
            setLoading(false);
            setError('This invitation link is missing a token.');
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const response = await axios.get(backendUrl('/auth/invite'), {
                    params: { token },
                    withCredentials: true
                });
                if (!cancelled) {
                    setPreview(response.data);
                }
            } catch (err) {
                if (!cancelled) {
                    setError(err.response?.data?.error || 'This invitation is invalid or has expired.');
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [token]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        if (formData.password.length < 15) {
            setError('Password must be at least 15 characters');
            return;
        }
        if (formData.password !== formData.confirmPassword) {
            setError('Passwords do not match');
            return;
        }
        setSaving(true);
        try {
            const response = await axios.post(backendUrl('/auth/accept-invite'), {
                token,
                password: formData.password,
                confirmPassword: formData.confirmPassword
            }, { withCredentials: true });
            setSuccess(response.data?.message || 'Invitation accepted. You can sign in with your username.');
            setTimeout(() => navigate('/login'), 2500);
        } catch (err) {
            setError(err.response?.data?.error || 'Could not accept this invitation.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="login-container">
            <div className="login-box">
                <h2>Accept Invitation</h2>
                <p className="subtitle">Join an existing Commray organization</p>

                {error && <div className="error-message">{error}</div>}
                {success && <div className="success-message">{success}</div>}

                {loading && <p>Loading invitation…</p>}

                {!loading && preview && !success && (
                    <>
                        <p className="subtitle" style={{ marginBottom: 16 }}>
                            You are joining <strong>{preview.organization || 'a Commray organization'}</strong>
                            {preview.username ? ` as ${preview.username}` : ''}.
                        </p>
                        <div className="form-group">
                            <label htmlFor="commray-accept-invite-tz">Time zone</label>
                            <UserTimezoneSelect showInnerLabel={false} selectId="commray-accept-invite-tz" embedded />
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="form-group">
                                <label htmlFor="password">Choose a password</label>
                                <input
                                    type="password"
                                    id="password"
                                    name="password"
                                    value={formData.password}
                                    onChange={(e) => setFormData((f) => ({ ...f, password: e.target.value }))}
                                    required
                                    disabled={saving}
                                    placeholder="At least 15 characters"
                                />
                                <PasswordHint value={formData.password} />
                            </div>
                            <div className="form-group">
                                <label htmlFor="confirmPassword">Confirm password</label>
                                <input
                                    type="password"
                                    id="confirmPassword"
                                    name="confirmPassword"
                                    value={formData.confirmPassword}
                                    onChange={(e) => setFormData((f) => ({ ...f, confirmPassword: e.target.value }))}
                                    required
                                    disabled={saving}
                                    placeholder="Confirm your password"
                                />
                            </div>
                            <button type="submit" disabled={saving} className="login-button">
                                {saving ? 'Joining…' : 'Join organization'}
                            </button>
                        </form>
                    </>
                )}

                <div className="signup-link">
                    <p><Link to="/login">Back to login</Link></p>
                </div>
            </div>
        </div>
    );
};

export default AcceptInvite;
